import { useState, useEffect } from "react";
import {
  Search,
  ChevronDown,
  ChevronUp,
  Calendar as CalendarIcon,
  CheckCircle,
  Truck,
  XCircle,
  Loader,
  AlertCircle
} from "lucide-react";
import api from "../../services/api";

export default function RetailersOrderPage() {
  const [orders, setOrders] = useState([]);
  const [expandedOrders, setExpandedOrders] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sortOrder, setSortOrder] = useState("desc"); // 'asc' or 'desc'
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [toast, setToast] = useState({ show: false, type: '', message: '' });
  const ordersPerPage = 5;

  const showToast = (type, message) => {
    setToast({ show: true, type, message });
    setTimeout(() => {
      setToast({ show: false, type: '', message: '' });
    }, 3000);
  };

  // Fetch orders on component mount
  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await api.getRetailerOrders();
      if (response.success) {
        // Transform API data to match our component structure with confirmation fields
        const transformedOrders = response.data.map(order => ({
          ...order,
          sellerConfirmed: order.seller_transaction_complete_confirmation === 1,
          buyerConfirmed: order.buyer_transaction_complete_confirmation === 1,
        }));
        setOrders(transformedOrders);
      } else {
        console.error('Failed to fetch orders:', response.message);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleOrderExpansion = (orderId) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  const handleConfirmBuyer = (order) => {
    setSelectedOrder(order);
    setShowConfirmModal(true);
  };

  const confirmBuyerTransaction = async () => {
    if (!selectedOrder) return;

    try {
      setConfirming(true);
      const result = await api.confirmBuyerTransaction(selectedOrder.order_id);
      
      if (result.success) {
        showToast('success', 'Buyer transaction confirmed successfully');
        
        // Update the order in local state
        setOrders(prevOrders => 
          prevOrders.map(order => 
            order.order_id === selectedOrder.order_id 
              ? { ...order, buyerConfirmed: true }
              : order
          )
        );
        
        setShowConfirmModal(false);
        setSelectedOrder(null);
      } else {
        showToast('error', result.message || 'Failed to confirm transaction');
      }
    } catch (error) {
      console.error('Error confirming buyer transaction:', error);
      showToast('error', 'Failed to confirm transaction');
    } finally {
      setConfirming(false);
    }
  };

  const filteredOrders = orders.filter((order) => {
    const matchesSearch =
      order.order_id.toString().includes(searchTerm.toLowerCase()) ||
      order.payment_reference.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.items.some((item) =>
        (item.product_name && item.product_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.farmer && item.farmer.name && item.farmer.name.toLowerCase().includes(searchTerm.toLowerCase()))
      );

    const matchesStatus =
      statusFilter === "All" || order.status.toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesStatus;
  });

  const sortedOrders = [...filteredOrders].sort((a, b) => {
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
  });

  // Pagination logic
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = sortedOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(sortedOrders.length / ordersPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
  };

  const getStatusClass = (status) => {
    switch (status.toLowerCase()) {
      case "completed":
        return "order-status-rtl completed-rtl";
      case "processing":
        return "order-status-rtl processing-rtl";
      case "shipped":
        return "order-status-rtl shipped-rtl";
      case "cancelled":
        return "order-status-rtl cancelled-rtl";
      case "pending":
        return "order-status-rtl processing-rtl";
      default:
        return "order-status-rtl";
    }
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case "completed":
        return <CheckCircle size={14} />;
      case "processing":
      case "pending":
        return <Loader size={14} className="animate-spin" />;
      case "shipped":
        return <Truck size={14} />;
      case "cancelled":
        return <XCircle size={14} />;
      default:
        return null;
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(parseFloat(amount));
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="orders-container-rtl">
        <div className="loading-container" style={{ textAlign: 'center', padding: '2rem' }}>
          <Loader className="animate-spin" size={32} />
          <p>Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-container-rtl">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast-notification-fxk toast-${toast.type}-fxk`}>
          <div className="toast-content-fxk">
            {toast.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
            <span>{toast.message}</span>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && selectedOrder && (
        <div className="modal-overlay_ffx">
          <div className="modal-content_ffx">
            <div className="modal-header_ffx">
              <h3>Confirm Transaction</h3>
              <button
                className="modal-close_ffx"
                onClick={() => {
                  setShowConfirmModal(false);
                  setSelectedOrder(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body_ffx">
              <div className="confirmation-content">
                <AlertCircle size={48} className="warning-icon" />
                <h4>Are you sure you want to confirm this transaction?</h4>
                <p>This will mark the buyer transaction as complete for order <strong>#{selectedOrder.order_id}</strong>.</p>
                
                <div className="order-summary">
                  <div className="order-info">
                    <span><strong>Order Reference:</strong> {selectedOrder.payment_reference}</span>
                    <span><strong>Total Amount:</strong> {formatCurrency(selectedOrder.total_amount)}</span>
                    <span><strong>Status:</strong> {selectedOrder.status}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer_ffx">
              <button
                className="cancel-button_ffx"
                onClick={() => {
                  setShowConfirmModal(false);
                  setSelectedOrder(null);
                }}
                disabled={confirming}
              >
                Cancel
              </button>
              <button
                className="confirm-button_ffx"
                onClick={confirmBuyerTransaction}
                disabled={confirming}
              >
                {confirming ? (
                  <>
                    <div className="loading-spinner"></div>
                    Confirming...
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} />
                    Confirm Transaction
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="orders-header-app">
        <h2 className="orders-title-app">My Orders</h2>
      </div>

      <div className="orders-search-bar-rtl">
        <div className="search-wrapper-rtl">
          <div className="search-icon-rtl">
            <Search size={18} />
          </div>
          <input
            type="text"
            className="search-input-rtl"
            placeholder="Search orders by ID, reference, or farmer"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="filters-rtl">
          <div className="filter-select-wrapper-rtl">
            <label htmlFor="status-filter" className="filter-label-rtl">
              Status:
            </label>
            <select
              id="status-filter"
              className="filter-select-rtl"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="All">All</option>
              <option value="Pending">Pending</option>
              <option value="Processing">Processing</option>
              <option value="Shipped">Shipped</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>

          <button
            className="sort-button-rtl"
            onClick={toggleSortOrder}
            aria-label="Toggle sort order"
          >
            <CalendarIcon size={16} />
            <span>Date</span>
            {sortOrder === "desc" ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronUp size={16} />
            )}
          </button>
        </div>
      </div>

      {/* Orders List */}
      <div className="orders-list-rtl">
        {currentOrders.length > 0 ? (
          currentOrders.map((order) => (
            <div key={order.order_id} className="order-card-rtl">
              <div
                className="order-header-rtl"
                onClick={() => toggleOrderExpansion(order.order_id)}
              >
                <div className="order-main-info-rtl">
                  <div className="order-id-wrapper-rtl">
                    <span className="order-id-label-rtl">Order ID:</span>
                    <span className="order-id-rtl">#{order.order_id}</span>
                  </div>
                  <div className="order-farmer-rtl">
                    Reference: {order.payment_reference}
                  </div>
                </div>

                <div className="order-secondary-info-rtl">
                  <div className={getStatusClass(order.status)}>
                    {getStatusIcon(order.status)}
                    <span>{order.status.charAt(0).toUpperCase() + order.status.slice(1)}</span>
                  </div>

                  <div className="order-date-rtl">
                    <CalendarIcon size={14} />
                    <span>{formatDate(order.created_at)}</span>
                  </div>

                  <div className="order-total-rtl">
                    {formatCurrency(order.total_amount)}
                  </div>
                  <button
                    className="expand-button-rtl"
                    aria-label="Expand order details"
                  >
                    {expandedOrders[order.order_id] ? (
                      <ChevronUp size={20} />
                    ) : (
                      <ChevronDown size={20} />
                    )}
                  </button>
                </div>
              </div>

              {expandedOrders[order.order_id] && (
                <div className="order-details-rtl">
                  <div className="order-info-section">
                    <div className="order-detail-item">
                      <strong>Shipping Address:</strong>
                      <span>{order.shipping_address}</span>
                    </div>
                    <div className="order-detail-item">
                      <strong>Phone:</strong>
                      <span>{order.phone}</span>
                    </div>
                  </div>

                  <div className="order-transaction-status">
                    <h4>Transaction Status</h4>
                    <div className="transaction-status-grid">
                      <div className="status-item">
                        <strong>Seller Confirmed:</strong>
                        <span className={order.sellerConfirmed ? 'confirmed' : 'not-confirmed'}>
                          {order.sellerConfirmed ? ' ✓ Yes' : ' ✗ No'}
                        </span>
                      </div>
                      <div className="status-item">
                        <strong>Buyer Confirmed:</strong>
                        <span className={order.buyerConfirmed ? 'confirmed' : 'not-confirmed'}>
                          {order.buyerConfirmed ? ' ✓ Yes' : ' ✗ No'}
                        </span>
                      </div>
                    </div>
                    {!order.buyerConfirmed && (
                      <button
                        className="confirm-buyer-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleConfirmBuyer(order);
                        }}
                      >
                        <CheckCircle size={16} />
                        Confirm Buyer Transaction
                      </button>
                    )}
                  </div>

                  <h4 className="order-items-title-rtl">Order Items</h4>
                  <table className="order-items-table-rtl">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Farmer</th>
                        <th>Quantity</th>
                        <th>Unit Price</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.items.map((item, index) => (
                        <tr key={index}>
                          <td>{item.product_name || 'Product'}</td>
                          <td>{item.farmer?.name || 'Unknown'}</td>
                          <td>{item.quantity}</td>
                          <td>{formatCurrency(item.price)}</td>
                          <td>{formatCurrency(item.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="4" className="order-total-label-rtl">
                          Total
                        </td>
                        <td className="order-grand-total-rtl">
                          {formatCurrency(order.total_amount)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="no-orders-rtl">
            <p>No orders found matching your search criteria.</p>
            {orders.length === 0 && (
              <p>You haven't placed any orders yet. Start shopping to see your orders here!</p>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {sortedOrders.length > 0 && (
        <div className="pagination-rtl">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-btn-rtl"
          >
            <ChevronDown className="rotate-90" />
          </button>
          
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
            <button
              key={number}
              onClick={() => paginate(number)}
              className={`pagination-btn-rtl ${currentPage === number ? 'active-rtl' : ''}`}
            >
              {number}
            </button>
          ))}
          
          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-btn-rtl"
          >
            <ChevronDown className="rotate-270" />
          </button>
        </div>
      )}
    </div>
  );
}